import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { brandColors } from '@config/brandTokens';

type ParentRole = 'mother' | 'father' | 'guardian';

export type HouseholdParent = {
  userId: string;
  playerId?: string | null;
  playerIdCode?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  parentRole: ParentRole;
  isYou?: boolean;
};

type HouseholdParentsData = {
  myRole?: ParentRole | null;
  myRoleSaved?: boolean;
  suggestedTheirRole?: ParentRole | null;
  allowedTheirRoles?: ParentRole[];
  parents?: HouseholdParent[];
  canLinkOtherParent?: boolean;
  canCreateChild?: boolean;
  maritalStatus?: 'individual' | 'married' | null;
  maxAdults?: number;
  adultCapMessage?: string | null;
};

const ADULT_CAP_COPY = 'Two adults can be linked for now. Extra guardians are not available yet.';

export const PARENT_ROLE_LABEL: Record<ParentRole, string> = {
  mother: 'Mother',
  father: 'Father',
  guardian: 'Guardian',
};

const ALL_ROLES: ParentRole[] = ['mother', 'father', 'guardian'];

function complementaryRole(role: ParentRole): ParentRole {
  if (role === 'mother') return 'father';
  if (role === 'father') return 'mother';
  return 'guardian';
}

function allowedTheirRolesFor(role: ParentRole): ParentRole[] {
  if (role === 'mother') return ['father', 'guardian'];
  if (role === 'father') return ['mother', 'guardian'];
  return ['mother', 'father', 'guardian'];
}

function pickTheirRole(myRole: ParentRole, current?: ParentRole | null): ParentRole {
  const allowed = allowedTheirRolesFor(myRole);
  if (current && allowed.includes(current)) return current;
  return complementaryRole(myRole);
}

function token() {
  return localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
}

async function readApiJson(res: Response) {
  const text = await res.text();
  if (!text.trim()) {
    throw new Error(
      res.status >= 500
        ? 'Cannot reach server. Is the backend running on port 5000?'
        : (res.statusText || 'Empty response'),
    );
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Invalid response');
  }
}

type FamilyStatusPayload = {
  myRole?: ParentRole | null;
  myRoleSaved?: boolean;
  maritalStatus?: 'individual' | 'married' | null;
  canCreateChild?: boolean;
};

interface HouseholdParentsBlockProps {
  defaultMyRole?: string | null;
  defaultGender?: string | null;
  onLinked?: () => void;
  onStatusChange?: (status: FamilyStatusPayload) => void;
  onParentsChange?: (parents: HouseholdParent[]) => void;
}

const HouseholdParentsBlock: React.FC<HouseholdParentsBlockProps> = ({
  defaultMyRole,
  defaultGender,
  onLinked,
  onStatusChange,
  onParentsChange,
}) => {
  const [data, setData] = useState<HouseholdParentsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const inferredFromGender: ParentRole =
    String(defaultGender || '').toLowerCase() === 'female'
      ? 'mother'
      : String(defaultGender || '').toLowerCase() === 'male'
        ? 'father'
        : 'guardian';
  const [myRole, setMyRole] = useState<ParentRole>(
    (defaultMyRole as ParentRole) || inferredFromGender,
  );
  const [roleSaved, setRoleSaved] = useState(false);
  const [theirRole, setTheirRole] = useState<ParentRole>(complementaryRole(myRole));

  const theirRoleOptions = (data?.allowedTheirRoles?.length
    ? data.allowedTheirRoles.filter((role) => allowedTheirRolesFor(myRole).includes(role))
    : allowedTheirRolesFor(myRole)
  );

  const applyPayload = (next: HouseholdParentsData) => {
    setData(next);
    const saved = !!next.myRoleSaved && !!next.myRole;
    setRoleSaved(saved);
    const nextMyRole = next.myRole || myRole;
    if (next.myRole) setMyRole(next.myRole);
    setTheirRole(pickTheirRole(nextMyRole, next.suggestedTheirRole));
    onStatusChange?.({
      myRole: next.myRole || null,
      myRoleSaved: saved,
      maritalStatus: next.maritalStatus ?? null,
      canCreateChild: !!next.canCreateChild,
    });
    onParentsChange?.(next.parents || []);
  };

  const load = async () => {
    try {
      const res = await fetch('/api/household/parents', {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const json = await readApiJson(res);
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to load parents');
      applyPayload(json.data as HouseholdParentsData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load parents');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveMyRole = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/household/parents/role', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ myRole }),
      });
      const json = await readApiJson(res);
      if (!res.ok || !json.success) throw new Error(json.message || 'Could not save relation');
      toast.success(json.message || 'Relation saved.');
      applyPayload(json.data as HouseholdParentsData);
      onLinked?.();
    } catch (err: any) {
      setError(err.message || 'Could not save relation');
    } finally {
      setSaving(false);
    }
  };

  const linkParent = async () => {
    if ((data?.parents || []).length >= (data?.maxAdults || 2) || data?.canLinkOtherParent === false) {
      setError(data?.adultCapMessage || ADULT_CAP_COPY);
      return;
    }
    const nextTheirRole = pickTheirRole(myRole, theirRole);
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/household/parents/link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
          myRole,
          theirRole: nextTheirRole,
        }),
      });
      const json = await readApiJson(res);
      if (!res.ok || !json.success) throw new Error(json.message || 'Could not link parent');
      toast.success(json.message || 'Parents linked.');
      setIdentifier('');
      applyPayload(json.data as HouseholdParentsData);
      onLinked?.();
    } catch (err: any) {
      setError(err.message || 'Could not link parent');
    } finally {
      setSaving(false);
    }
  };

  const linkedParents = data?.parents || [];
  const maxAdults = data?.maxAdults || 2;
  const atAdultCap = linkedParents.length >= maxAdults;
  const canLink = !!data && roleSaved && !atAdultCap && data.canLinkOtherParent !== false;
  const capCopy = data?.adultCapMessage || ADULT_CAP_COPY;
  const selectedTheirRole = theirRoleOptions.includes(theirRole)
    ? theirRole
    : complementaryRole(myRole);

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid #e2e8f0',
        borderRadius: 1.5,
        bgcolor: '#f8fafc',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {atAdultCap && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: roleSaved ? 0 : 1.5 }}>
          {capCopy}
        </Typography>
      )}

      {!roleSaved && (
        <Stack spacing={1.25}>
          <Typography sx={{ fontWeight: 700, color: brandColors.navy }}>
            Your relation
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField
              select
              size="small"
              label="Your relation"
              value={myRole}
              onChange={(e) => {
                const next = e.target.value as ParentRole;
                setMyRole(next);
                setTheirRole(pickTheirRole(next));
              }}
              disabled={saving}
              sx={{ minWidth: 180 }}
            >
              {ALL_ROLES.map((role) => (
                <MenuItem key={role} value={role}>
                  {PARENT_ROLE_LABEL[role]}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              disabled={saving}
              onClick={saveMyRole}
              sx={{ textTransform: 'none', fontWeight: 800, bgcolor: brandColors.navy, whiteSpace: 'nowrap' }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      )}

      {roleSaved && !atAdultCap && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Player ID or email"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            disabled={!canLink || saving}
            fullWidth
          />
          <TextField
            select
            size="small"
            label="Their relation"
            value={selectedTheirRole}
            onChange={(e) => setTheirRole(e.target.value as ParentRole)}
            disabled={!canLink || saving}
            sx={{ minWidth: 180 }}
          >
            {theirRoleOptions.map((role) => (
              <MenuItem key={role} value={role}>
                {PARENT_ROLE_LABEL[role]}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            disabled={!canLink || saving || !identifier.trim()}
            onClick={linkParent}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: brandColors.navy, whiteSpace: 'nowrap' }}
          >
            {saving ? 'Linking...' : 'Link'}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default HouseholdParentsBlock;
